import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Car, Package, CheckCircle, MailOpen, Eye, Star, Search, RotateCcw, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VehicleFormModal } from "@/components/VehicleFormModal";
import { SiteSettings } from "@/components/SiteSettings";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";

const placeholder =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=400&q=70";

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [invFilter, setInvFilter] = useState("all"); // all | available | sold
  const [inquiries, setInquiries] = useState([]);
  const [inquirySearch, setInquirySearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, s, iq] = await Promise.all([
        api.get("/admin/vehicles"),
        api.get("/admin/stats"),
        api.get("/admin/inquiries"),
      ]);
      setVehicles(v.data);
      setStats(s.data);
      setInquiries(iq.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (id) => {
    try {
      await api.patch(`/admin/vehicles/${id}/status`);
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const toggleFeatured = async (id) => {
    try {
      await api.patch(`/admin/vehicles/${id}/featured`);
      load();
    } catch {
      toast.error("Failed to update featured");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/vehicles/${deleteId}`);
      toast.success("Vehicle deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (v) => { setEditing(v); setModalOpen(true); };
  const doLogout = () => { logout(); navigate("/"); };

  const statCards = stats
    ? [
        { icon: Package, label: "Total Vehicles", value: stats.total },
        { icon: Car, label: "Available", value: stats.available },
        { icon: CheckCircle, label: "Sold", value: stats.sold },
        { icon: MailOpen, label: "Inquiries", value: stats.inquiries },
        { icon: Eye, label: "Site Visits", value: stats.total_views },
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="admin-dashboard">
      <header className="border-b border-slate-800 sticky top-0 bg-slate-900/95 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center"><Car size={18} /></span>
            <span className="font-display font-bold">Erskine Auto · Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => window.open("/", "_blank")} className="text-slate-300 hover:text-white" data-testid="admin-view-site">
              <ExternalLink size={16} className="mr-1" /> View Site
            </Button>
            <Button variant="ghost" size="sm" onClick={doLogout} className="text-slate-300 hover:text-white" data-testid="admin-logout">
              <LogOut size={16} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((c) => (
            <div key={c.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4" data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
              <c.icon size={20} className="text-red-500 mb-2" />
              <p className="text-2xl font-black">{c.value}</p>
              <p className="text-xs text-slate-400">{c.label}</p>
            </div>
          ))}
        </div>

        {stats && (
          <div className="grid lg:grid-cols-2 gap-4 mb-8">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5" data-testid="traffic-chart">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-display font-bold text-sm">Visits — Last 7 Days</h3>
                <span className="text-xs text-slate-400">{stats.total_views} total</span>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Home {stats.views_home} · Inventory {stats.views_inventory} · Vehicles {stats.views_vehicle}
              </p>
              <div className="flex items-end gap-2 h-28">
                {stats.daily_views.map((d) => {
                  const max = Math.max(...stats.daily_views.map((x) => x.count), 1);
                  const h = Math.round((d.count / max) * 100);
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-slate-400">{d.count}</span>
                      <div className="w-full bg-red-600/80 rounded-t transition-all" style={{ height: d.count === 0 ? "2px" : `${Math.max(h, 6)}%` }} />
                      <span className="text-[10px] text-slate-500">{d.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5" data-testid="traffic-top-vehicles">
              <h3 className="font-display font-bold text-sm mb-4">Most Viewed Vehicles</h3>
              {stats.top_vehicles.length === 0 ? (
                <p className="text-sm text-slate-500">No vehicle views yet.</p>
              ) : (
                <div className="space-y-3">
                  {stats.top_vehicles.map((v, i) => (
                    <div key={`${v.label}-${i}`} className="flex items-center gap-3 text-sm">
                      <span className="w-5 text-slate-500">{i + 1}</span>
                      <span className="flex-1 truncate">{v.label}</span>
                      <span className="flex items-center gap-1 text-slate-400"><Eye size={13} /> {v.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <SiteSettings />

        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h1 className="font-display font-bold text-xl">Manage Inventory</h1>
          <div className="flex items-center gap-3">
            <div className="flex bg-slate-800 border border-slate-700 rounded-full p-1" data-testid="inventory-status-filter">
              {["all", "available", "sold"].map((f) => (
                <button
                  key={f}
                  onClick={() => setInvFilter(f)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full capitalize transition-colors ${
                    invFilter === f ? "bg-red-600 text-white" : "text-slate-400 hover:text-white"
                  }`}
                  data-testid={`inv-filter-${f}`}
                >
                  {f === "sold" ? "Sold Archive" : f}
                </button>
              ))}
            </div>
            <Button onClick={openAdd} className="bg-red-600 hover:bg-red-700 font-semibold rounded-full" data-testid="admin-add-vehicle">
              <Plus size={18} className="mr-1" /> Add Vehicle
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20 text-slate-500"><Loader2 className="animate-spin" size={28} /></div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-20 text-slate-500 border border-dashed border-slate-700 rounded-xl">
            No vehicles yet. Click "Add Vehicle" to list your first one.
          </div>
        ) : (
          <div className="space-y-3">
            {vehicles.filter((v) => invFilter === "all" || v.status === invFilter).length === 0 ? (
              <div className="text-center py-12 text-slate-500 border border-dashed border-slate-700 rounded-xl" data-testid="inv-empty">
                {invFilter === "sold" ? "No sold vehicles in the archive yet." : `No ${invFilter} vehicles.`}
              </div>
            ) : vehicles.filter((v) => invFilter === "all" || v.status === invFilter).map((v) => {
              const mainPath = v.photos?.[v.main_photo_index] || v.photos?.[0];
              const isSold = v.status === "sold";
              return (
                <div key={v.id} className="bg-slate-800 border border-slate-700 rounded-xl p-3 flex items-center gap-4" data-testid={`admin-vehicle-row-${v.id}`}>
                  <img src={mainPath ? fileUrl(mainPath) : placeholder} alt="" className="w-20 h-16 rounded-lg object-cover shrink-0 bg-slate-700" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate flex items-center gap-2">
                      {v.year} {v.make} {v.model}
                      {v.featured && <Star size={13} className="text-amber-400 shrink-0" fill="currentColor" />}
                    </p>
                    <p className="text-sm text-slate-400">${v.price.toLocaleString()} · {v.mileage.toLocaleString()} km · {v.body_type}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => toggleFeatured(v.id)}
                      title={v.featured ? "Unfeature" : "Feature on home page"}
                      className={`p-2 rounded-full transition-colors ${v.featured ? "text-amber-400 hover:text-amber-300" : "text-slate-500 hover:text-amber-400"}`}
                      data-testid={`admin-toggle-featured-${v.id}`}
                    >
                      <Star size={16} fill={v.featured ? "currentColor" : "none"} />
                    </button>
                    {isSold ? (
                      <Button size="sm" variant="outline" onClick={() => toggleStatus(v.id)}
                        className="rounded-full border-green-600/50 text-green-400 hover:bg-green-600 hover:text-white" data-testid={`admin-relist-${v.id}`}>
                        <RotateCcw size={14} className="mr-1" /> Relist
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 mr-2">
                        <span className="text-xs font-medium text-green-400">Available</span>
                        <Switch checked onCheckedChange={() => toggleStatus(v.id)} data-testid={`admin-toggle-status-${v.id}`} />
                      </div>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(v)} className="text-slate-300 hover:text-white" data-testid={`admin-edit-${v.id}`}>
                      <Pencil size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteId(v.id)} className="text-slate-300 hover:text-rose-500" data-testid={`admin-delete-${v.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10" data-testid="inquiry-inbox">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <h2 className="font-display font-bold text-lg">Inquiry Inbox {inquiries.length > 0 && <span className="text-slate-500 text-sm font-normal">({inquiries.length})</span>}</h2>
            {inquiries.length > 0 && (
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  value={inquirySearch}
                  onChange={(e) => setInquirySearch(e.target.value)}
                  placeholder="Search inquiries…"
                  className="pl-9 w-64 bg-slate-800 border-slate-700 text-white"
                  data-testid="inquiry-search"
                />
              </div>
            )}
          </div>
          {(() => {
            const q = inquirySearch.trim().toLowerCase();
            const list = q
              ? inquiries.filter((i) =>
                  [i.name, i.vehicle_label, i.message, i.phone, i.email].join(" ").toLowerCase().includes(q))
              : inquiries;
            if (inquiries.length === 0)
              return <p className="text-slate-500 text-sm">No inquiries yet. They'll appear here when buyers reach out.</p>;
            if (list.length === 0)
              return <p className="text-slate-500 text-sm" data-testid="inquiry-no-match">No inquiries match "{inquirySearch}".</p>;
            return (
              <div className="space-y-2">
                {list.map((inq) => (
                  <div key={inq.id} className="bg-slate-800 border border-slate-700 rounded-xl p-3 text-sm" data-testid={`inquiry-row-${inq.id}`}>
                    <div className="flex justify-between gap-2">
                      <span className="font-semibold">{inq.name} · {inq.vehicle_label}</span>
                      <span className="text-slate-500 text-xs whitespace-nowrap">{new Date(inq.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-slate-400 mt-1">
                      <a href={`tel:${inq.phone}`} className="hover:text-red-400">{inq.phone}</a> ·{" "}
                      <a href={`mailto:${inq.email}`} className="hover:text-red-400">{inq.email}</a>
                      {inq.financing && <span className="ml-2 text-amber-400">· Financing</span>}
                    </p>
                    <p className="text-slate-300 mt-1">{inq.message}</p>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      <VehicleFormModal open={modalOpen} onClose={() => setModalOpen(false)} vehicle={editing} onSaved={load} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this vehicle?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the listing and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
